import "./new-password-form.scss";

import { useState } from "react";
import { useHistory } from "react-router-dom";

import useUpdatePassword from "@hooks/auth/useUpdatePassword";
import { useTitle } from "@hooks/useTitle";
import {
    Button, Card, FormGroup, Label, TextField, Title
} from "@runwayhealth/runway-components-react";
import { toastSuccess } from "@utils/utils";

type NewPasswordFormProps = {
  authToken: string;
  resetToken: string;
};

const NewPasswordForm = ({ authToken, resetToken }: NewPasswordFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [requestError, setRequestError] = useState("");
  const { updatePassword } = useUpdatePassword();
  const history = useHistory();
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  useTitle("Set new password");

  const saveNewPassword = async () => {
    if (errorText === "") {
      setIsLoading(true);
      const result = await updatePassword({
        newPassword,
        resetToken,
        authToken,
      });

      if (result.message !== "success") {
        setRequestError(result.message);
        setIsLoading(false);
        return;
      }
      toastSuccess("Success!", "You may now login with your new password");
      history.push("/login");
      setIsLoading(false);
    } else if (confirmNewPassword === "" || newPassword === "") {
      setErrorText("Password fields can't be empty");
    }
  };

  const validatePassword = () => {
    const passwordPattern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    if (!passwordPattern.test(newPassword)) {
      setErrorText("Please enter a valid password");
      return false;
    }

    if (newPassword !== confirmNewPassword) {
      setErrorText("Passwords don't match");
      return false;
    }

    setErrorText("");
    return true;
  };

  const PasswordErrorGuide = () => (
    <div>
      <h5 className="error-message">{errorText}</h5>
      <h5 className="error-message">{requestError}</h5>
      {errorText !== "" ? (
        <Card>
          <span style={{ fontSize: " 16px", marginLeft: "36px" }}>Your new password must:</span>
          <ul style={{ marginLeft: "15px" }}>
            <li>Contain at least 8 characters</li>
            <li>Contain at least 1 number</li>
            <li>Contain at least 1 uppercase letter</li>
            <li>Be different from your previous password</li>
          </ul>
        </Card>
      ) : null}
    </div>
  );

  return (
    <Card>
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <Title>Set your new password</Title>
      </div>
      <FormGroup>
        <Label>Enter your password.</Label>
        <TextField
          name="password"
          type="password"
          label="New password"
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            setConfirmNewPassword(e.currentTarget.value)
          }
          onKeyUp={validatePassword}
        />
      </FormGroup>
      <FormGroup>
        <Label>Confirm your new password.</Label>
        <TextField
          name="confirm"
          type="password"
          label="Confirm password"
          onChange={(e: React.FormEvent<HTMLInputElement>) => setNewPassword(e.currentTarget.value)}
          onKeyUp={validatePassword}
        />
      </FormGroup>
      <PasswordErrorGuide />
      <div style={{ marginTop: "20px" }}>
        <Button
          size="lg"
          onClick={saveNewPassword}
          iconName="ArrowRight"
          isLoading={isLoading}
          disabled={isLoading}
          isBlock
        >
          Continue
        </Button>
      </div>
    </Card>
  );
};

export default NewPasswordForm;
